import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  EventAvailable as EventAvailableIcon,
  Payments as PaymentsIcon,
  LocalAtm as LocalAtmIcon,
  HowToReg as HowToRegIcon,
  School as SchoolIcon,
  EmojiEvents as EmojiEventsIcon,
  ArrowForward as ArrowForwardIcon,
  Cake as CakeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { fetchDailyDashboard } from "../../utils/api/api";
import PageLoader from "../../utils/components/PageLoader";
import { useSystemButtonStyles } from "../../hooks/useSystemButtonStyles";
import { getMm, getPageTitleSx } from "../../theme/modernMaterialEnterprise";
import {
  getFocusVisibleSx,
  getGlassSurfaceSx,
  getInteractiveCardSx,
  getLandingKpiTint,
} from "../../theme/landingDashboardStyles";

const COLLAPSED_CARDS_STORAGE_PREFIX = "staffDashboardCollapsedCards";

function getCollapsedCardsStorageKey(clubID) {
  return clubID != null ? `${COLLAPSED_CARDS_STORAGE_PREFIX}_${clubID}` : COLLAPSED_CARDS_STORAGE_PREFIX;
}

function readCollapsedCards(clubID) {
  try {
    const raw = localStorage.getItem(getCollapsedCardsStorageKey(clubID));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeCollapsedCards(clubID, state) {
  try {
    localStorage.setItem(getCollapsedCardsStorageKey(clubID), JSON.stringify(state));
  } catch {
    /* ignore quota / private mode */
  }
}

function formatMoney(amount, currency, locale) {
  const value = Number(amount) || 0;
  try {
    return new Intl.NumberFormat(locale || undefined, {
      style: "currency",
      currency: currency || "SEK",
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${Math.round(value)} ${currency || "SEK"}`;
  }
}

function formatTimeRange(start, end) {
  if (!start && !end) return "";
  if (start && end) return `${start}–${end}`;
  return start || end || "";
}

function ScheduleClassRow({ cls, isDarkMode, t }) {
  const metaParts = [
    cls.groupName,
    cls.mat ? `${t("schedule_mat")} ${cls.mat}` : null,
  ].filter(Boolean);

  return (
    <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.8125rem",
          color: getMm(isDarkMode).onSurface,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}
      >
        {cls.title}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.75rem",
          color: getMm(isDarkMode).onSurfaceVariant,
          mb: metaParts.length > 0 || cls.attendanceCount != null ? 0.35 : 0,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}
      >
        {formatTimeRange(cls.startTime, cls.endTime)}
      </Typography>
      {metaParts.length > 0 && (
        <Typography
          sx={{
            fontSize: "0.6875rem",
            lineHeight: 1.35,
            color: getMm(isDarkMode).onSurfaceVariant,
            mb: cls.attendanceCount != null ? 0.35 : 0,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            wordBreak: "break-word",
          }}
        >
          {metaParts.join(" · ")}
        </Typography>
      )}
      {cls.attendanceCount != null && (
        <Typography
          sx={{
            fontSize: "0.6875rem",
            fontWeight: 600,
            color: getMm(isDarkMode).secondary,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {t("staff_dashboard_attendance_count", { count: cls.attendanceCount })}
        </Typography>
      )}
    </Box>
  );
}

function InstructorClassRow({ cls, isDarkMode, t }) {
  const border = getMm(isDarkMode).outlineVariant;
  const yesNames = cls.yesInstructors?.length
    ? cls.yesInstructors.join(", ")
    : t("staff_dashboard_instructor_none");
  const noNames = cls.noInstructors?.length
    ? cls.noInstructors.join(", ")
    : t("staff_dashboard_instructor_none");

  const columnShellSx = {
    flex: 1,
    minWidth: 0,
    px: 0.75,
    py: 0.5,
  };

  const columnLabelSx = (accent) => ({
    fontSize: "0.625rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: accent,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    mb: 0.25,
  });

  const columnNamesSx = {
    fontSize: "0.6875rem",
    lineHeight: 1.35,
    color: getMm(isDarkMode).onSurfaceVariant,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    wordBreak: "break-word",
  };

  return (
    <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.8125rem",
          color: getMm(isDarkMode).onSurface,
        }}
      >
        {cls.title}
      </Typography>
      <Typography sx={{ fontSize: "0.75rem", color: getMm(isDarkMode).onSurfaceVariant, mb: 0.5 }}>
        {formatTimeRange(cls.startTime, cls.endTime)}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          border: `1px solid ${border}`,
          borderRadius: "6px",
          overflow: "hidden",
          textAlign: { xs: "center", sm: "left" },
        }}
      >
        <Box
          sx={{
            ...columnShellSx,
            borderRight: { xs: "none", sm: `1px solid ${border}` },
            borderBottom: { xs: `1px solid ${border}`, sm: "none" },
            backgroundColor: isDarkMode ? "rgba(132, 212, 211, 0.10)" : "#E6F6F6",
          }}
        >
          <Typography sx={columnLabelSx(getMm(isDarkMode).secondary)}>
            {t("staff_dashboard_col_available")} · {cls.yesCount ?? 0}
          </Typography>
          <Typography sx={columnNamesSx}>{yesNames}</Typography>
        </Box>
        <Box
          sx={{
            ...columnShellSx,
            backgroundColor: isDarkMode ? "rgba(255, 179, 179, 0.10)" : "#FDECEC",
          }}
        >
          <Typography sx={columnLabelSx(getMm(isDarkMode).tertiary)}>
            {t("staff_dashboard_col_unavailable")} · {cls.noCount ?? 0}
          </Typography>
          <Typography sx={columnNamesSx}>{noNames}</Typography>
        </Box>
      </Box>
    </Box>
  );
}

const DASHBOARD_CARD_HEIGHT = { xs: "auto", sm: 240 };
const DASHBOARD_CARD_BODY_MAX = { xs: 280, sm: "100%" };
const BIRTHDAY_BANNER_MAX_HEIGHT = { xs: 260, sm: 200 };

function kpiIconTint(isDarkMode, variant) {
  const mapped = variant === "violet" ? "accent" : variant;
  return getLandingKpiTint(isDarkMode, mapped);
}

const dashboardScrollSx = (isDarkMode) => ({
  overflowY: "auto",
  scrollbarWidth: "thin",
  scrollbarColor: isDarkMode ? "#4B5563 transparent" : "#D1D5DB transparent",
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: 999,
    backgroundColor: getMm(isDarkMode).outline,
  },
});

function DashboardViewMoreButton({ label, onClick }) {
  const { dashboardArrowButtonSx } = useSystemButtonStyles();

  return (
    <Tooltip title={label} arrow>
      <IconButton
        onClick={onClick}
        aria-label={label}
        sx={{
          ...dashboardArrowButtonSx,
          ...getFocusVisibleSx(false),
          cursor: "pointer",
          minWidth: 44,
          minHeight: 44,
        }}
      >
        <ArrowForwardIcon />
      </IconButton>
    </Tooltip>
  );
}

function DashboardSectionCard({
  cardId,
  title,
  icon,
  iconVariant = "primary",
  children,
  isDarkMode,
  viewPage,
  onNavigate,
  viewMoreLabel,
  collapsed,
  isCollapsible,
  onToggleCollapse,
  collapseLabel,
  expandLabel,
}) {
  const tint = kpiIconTint(isDarkMode, iconVariant);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        ...getGlassSurfaceSx(isDarkMode),
        height: collapsed ? "auto" : DASHBOARD_CARD_HEIGHT,
        minHeight: collapsed ? "auto" : { xs: 0, sm: 240 },
        width: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        fontFamily: getMm(isDarkMode).fontBody,
      }}
    >
      <CardHeader
        avatar={
          <Avatar
            variant="rounded"
            sx={{
              width: { xs: 36, sm: 40 },
              height: { xs: 36, sm: 40 },
              bgcolor: tint.bg,
              color: tint.fg,
              borderRadius: "10px",
              flexShrink: 0,
            }}
          >
            {React.isValidElement(icon)
              ? React.cloneElement(icon, { sx: { fontSize: isMobile ? 20 : 22, ...(icon.props?.sx || {}) } })
              : icon}
          </Avatar>
        }
        action={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flexShrink: 0 }}>
            {viewPage && onNavigate && !collapsed ? (
              <DashboardViewMoreButton
                label={viewMoreLabel}
                onClick={() => onNavigate(viewPage)}
              />
            ) : null}
            {isCollapsible ? (
              <Tooltip title={collapsed ? expandLabel : collapseLabel} arrow>
                <IconButton
                  size="small"
                  onClick={() => onToggleCollapse?.(cardId)}
                  aria-expanded={!collapsed}
                  sx={{
                    color: getMm(isDarkMode).onSurfaceVariant,
                    ...getFocusVisibleSx(isDarkMode),
                    minWidth: 44,
                    minHeight: 44,
                  }}
                >
                  {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        }
        title={
          <Typography
            component="span"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "0.8125rem", sm: "0.9375rem" },
              color: getMm(isDarkMode).onSurface,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              lineHeight: 1.35,
              wordBreak: "break-word",
            }}
          >
            {title}
          </Typography>
        }
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1.25, sm: 1.25 },
          alignItems: isMobile ? "center" : "flex-start",
          flexDirection: isMobile ? "column" : "row",
          textAlign: isMobile ? "center" : "left",
          gap: isMobile ? 1 : 0,
          "& .MuiCardHeader-avatar": {
            mr: isMobile ? 0 : 1.5,
            mb: isMobile ? 0.5 : 0,
          },
          "& .MuiCardHeader-content": {
            minWidth: 0,
            flex: isMobile ? "none" : "1 1 120px",
            width: isMobile ? "100%" : undefined,
          },
          "& .MuiCardHeader-action": {
            m: 0,
            alignSelf: isMobile ? "center" : "flex-start",
            ml: isMobile ? 0 : "auto",
          },
        }}
      />
      {!collapsed && (
        <CardContent
          sx={{
            flex: 1,
            pt: 0,
            px: { xs: 1.5, sm: 2 },
            pb: { xs: 1.5, sm: 2 },
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
            textAlign: { xs: "center", sm: "left" },
            "&:last-child": { pb: { xs: 1.5, sm: 2 } },
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              maxHeight: collapsed ? undefined : DASHBOARD_CARD_BODY_MAX,
              overflowY: "auto",
              ...dashboardScrollSx(isDarkMode),
              "& .MuiButton-root": {
                mx: { xs: "auto", sm: 0 },
                display: { xs: "flex", sm: "inline-flex" },
              },
            }}
          >
            {children}
          </Box>
        </CardContent>
      )}
    </Card>
  );
}

/**
 * Mini statistics card (Material Dashboard–style): icon well + label + metric + caption.
 * @see https://mui.com/material-ui/react-card/
 */
function DashboardKpiCard({ title, value, subtitle, icon, iconVariant = "primary", isDarkMode, onClick, actionLabel }) {
  const tint = kpiIconTint(isDarkMode, iconVariant);
  const mm = getMm(isDarkMode);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const mobileTextSx = isMobile
    ? { textAlign: "center", width: "100%" }
    : {};

  const body = (
    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
      <Stack
        direction={isMobile ? "column" : "row"}
        spacing={isMobile ? 1.25 : 1.75}
        alignItems={isMobile ? "center" : "flex-start"}
        sx={mobileTextSx}
      >
        <Avatar
          variant="rounded"
          sx={{
            width: { xs: 44, sm: 48 },
            height: { xs: 44, sm: 48 },
            bgcolor: tint.bg,
            color: tint.fg,
            borderRadius: "12px",
            flexShrink: 0,
          }}
        >
          {React.isValidElement(icon)
            ? React.cloneElement(icon, { sx: { fontSize: isMobile ? 24 : 24, ...(icon.props?.sx || {}) } })
            : icon}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1, width: "100%", ...mobileTextSx }}>
          <Typography
            variant="body2"
            sx={{
              color: mm.onSurfaceVariant,
              fontWeight: isMobile ? 600 : 500,
              fontFamily: mm.fontBody,
              lineHeight: 1.35,
              ...mobileTextSx,
            }}
          >
            {title}
          </Typography>
          <Typography
            component="p"
            sx={{
              fontWeight: 700,
              color: mm.onSurface,
              fontFamily: mm.fontDisplay,
              lineHeight: 1.15,
              mt: 0.5,
              letterSpacing: "-0.02em",
              fontSize: { xs: "1.375rem", sm: "1.5rem" },
              wordBreak: "break-word",
              ...mobileTextSx,
            }}
          >
            {value}
          </Typography>
          {subtitle ? (
            <Typography
              variant="caption"
              sx={{
                display: isMobile ? "block" : "-webkit-box",
                WebkitLineClamp: isMobile ? undefined : 6,
                WebkitBoxOrient: isMobile ? undefined : "vertical",
                overflow: isMobile ? "visible" : "hidden",
                mt: 0.65,
                color: mm.onSurfaceVariant,
                fontFamily: mm.fontBody,
                lineHeight: 1.4,
                wordBreak: "break-word",
                maxWidth: isMobile ? 320 : undefined,
                mx: isMobile ? "auto" : undefined,
                ...mobileTextSx,
              }}
            >
              {subtitle}
            </Typography>
          ) : null}
          {onClick && actionLabel ? (
            <Typography
              variant="caption"
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.25,
                mt: 0.75,
                fontWeight: 600,
                color: mm.secondary,
                fontFamily: mm.fontBody,
                ...(isMobile ? { width: "100%" } : {}),
              }}
            >
              {actionLabel}
              <ArrowForwardIcon sx={{ fontSize: 14 }} aria-hidden="true" />
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </CardContent>
  );

  return (
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: 2,
        fontFamily: mm.fontBody,
        ...getInteractiveCardSx(isDarkMode, { clickable: Boolean(onClick) }),
      }}
    >
      {onClick ? (
        <CardActionArea
          onClick={onClick}
          aria-label={typeof title === "string" ? `${title}. ${actionLabel || ""}`.trim() : undefined}
          sx={{
            height: "100%",
            alignItems: isMobile ? "center" : "stretch",
            justifyContent: isMobile ? "center" : "flex-start",
            ...getFocusVisibleSx(isDarkMode),
          }}
        >
          {body}
        </CardActionArea>
      ) : (
        body
      )}
    </Card>
  );
}

function BirthdayPersonMiniCard({ person, isDarkMode, t }) {
  const ageLabel =
    person.ageTurning != null
      ? t("staff_dashboard_birthdays_turns", { age: person.ageTurning })
      : null;

  return (
    <Paper
      variant="outlined"
      sx={{
        width: "100%",
        p: 0.75,
        height: "100%",
        borderRadius: 1.5,
        borderColor: getMm(isDarkMode).outlineVariant,
        bgcolor: getMm(isDarkMode).surfaceLow,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        minHeight: 52,
      }}
    >
      <Typography
        sx={{
          width: "100%",
          fontWeight: 600,
          fontSize: { xs: "0.8125rem", sm: "0.875rem" },
          lineHeight: 1.3,
          color: getMm(isDarkMode).onSurface,
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        {person.name}
      </Typography>
      {ageLabel ? (
        <Typography
          variant="caption"
          sx={{
            mt: 0.25,
            fontSize: "0.625rem",
            color: getMm(isDarkMode).onSurfaceVariant,
            lineHeight: 1.2,
          }}
        >
          {ageLabel}
        </Typography>
      ) : null}
    </Paper>
  );
}

function BirthdayDayPanel({ title, dateLabel, people, isDarkMode, t, emptyLabel }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 0.75, sm: 1 },
        height: "100%",
        borderRadius: 1.5,
        borderColor: getMm(isDarkMode).outlineVariant,
        bgcolor: getMm(isDarkMode).surfaceLowest,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        textAlign: { xs: "center", sm: "left" },
      }}
    >
      <Stack
        direction={isMobile ? "column" : "row"}
        alignItems="center"
        justifyContent={isMobile ? "center" : "space-between"}
        spacing={0.75}
        sx={{ mb: 0.5, minWidth: 0, width: "100%" }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.75rem",
            color: getMm(isDarkMode).onSurface,
            lineHeight: 1.35,
            minWidth: 0,
            wordBreak: "break-word",
          }}
        >
          {title}
          <Typography
            component="span"
            sx={{
              fontWeight: 500,
              fontSize: "0.6875rem",
              color: getMm(isDarkMode).onSurfaceVariant,
              ml: { xs: 0, sm: 0.5 },
              display: { xs: "block", sm: "inline" },
              mt: { xs: 0.15, sm: 0 },
            }}
          >
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              {" · "}
            </Box>
            {dateLabel}
          </Typography>
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            fontSize: "0.6875rem",
            color: getMm(isDarkMode).onSurfaceVariant,
            flexShrink: 0,
          }}
        >
          {people.length}
        </Typography>
      </Stack>
      {people.length > 0 ? (
        <Grid container spacing={0.75}>
          {people.map((person) => (
            <Grid key={person.studentID} item xs={6} sm={4} sx={{ display: "flex" }}>
              <BirthdayPersonMiniCard person={person} isDarkMode={isDarkMode} t={t} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography
          variant="caption"
          sx={{
            color: getMm(isDarkMode).onSurfaceVariant,
            fontStyle: "italic",
            py: 0.25,
            display: "block",
            textAlign: "center",
          }}
        >
          {emptyLabel}
        </Typography>
      )}
    </Paper>
  );
}

function BirthdaysBannerCard({
  birthdays,
  isDarkMode,
  t,
  todayLabel,
  tomorrowLabel,
  collapsed,
  isCollapsible,
  onToggleCollapse,
  collapseLabel,
  expandLabel,
}) {
  const todayList = birthdays?.today || [];
  const tomorrowList = birthdays?.tomorrow || [];
  const tint = kpiIconTint(isDarkMode, "primary");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const subheaderParts = [];
  if (todayList.length > 0) {
    subheaderParts.push(
      t("staff_dashboard_birthdays_subheader_today", { count: todayList.length })
    );
  }
  if (tomorrowList.length > 0) {
    subheaderParts.push(
      t("staff_dashboard_birthdays_subheader_tomorrow", { count: tomorrowList.length })
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        ...getGlassSurfaceSx(isDarkMode),
        width: "100%",
        fontFamily: getMm(isDarkMode).fontBody,
        overflow: "hidden",
      }}
    >
      <CardHeader
        avatar={
          <Avatar
            variant="rounded"
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              bgcolor: tint.bg,
              color: tint.fg,
            }}
          >
            <CakeIcon sx={{ fontSize: 20 }} />
          </Avatar>
        }
        action={
          isCollapsible ? (
            <Tooltip title={collapsed ? expandLabel : collapseLabel} arrow>
              <IconButton
                size="small"
                onClick={() => onToggleCollapse?.("birthdays")}
                aria-expanded={!collapsed}
                sx={{
                  color: getMm(isDarkMode).onSurfaceVariant,
                  minWidth: 44,
                  minHeight: 44,
                }}
              >
                {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          ) : null
        }
        title={
          <Typography
            component="span"
            sx={{
              fontWeight: 700,
              fontSize: "0.875rem",
              color: getMm(isDarkMode).onSurface,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {t("staff_dashboard_birthdays_title")}
          </Typography>
        }
        subheader={
          subheaderParts.length > 0 ? (
            <Typography
              component="span"
              variant="caption"
              sx={{
                color: getMm(isDarkMode).onSurfaceVariant,
                display: "block",
                mt: 0.15,
                lineHeight: 1.4,
                wordBreak: "break-word",
              }}
            >
              {subheaderParts.join(" · ")}
            </Typography>
          ) : null
        }
        sx={{
          px: { xs: 1.5, sm: 1.5 },
          py: { xs: 1, sm: 0.75 },
          alignItems: isMobile ? "center" : "flex-start",
          flexDirection: isMobile ? "column" : "row",
          textAlign: isMobile ? "center" : "left",
          gap: isMobile ? 0.75 : 0,
          "& .MuiCardHeader-avatar": { mr: isMobile ? 0 : 1, mb: isMobile ? 0.25 : 0 },
          "& .MuiCardHeader-content": { minWidth: 0, width: isMobile ? "100%" : undefined },
          "& .MuiCardHeader-action": {
            m: 0,
            alignSelf: isMobile ? "center" : "flex-start",
            flexShrink: 0,
          },
        }}
      />

      <Collapse in={!collapsed} timeout={prefersReducedMotion ? 0 : "auto"} unmountOnExit>
        <CardContent
          sx={{
            pt: 0,
            px: { xs: 1.25, sm: 1.5 },
            pb: { xs: 1, sm: 1.25 },
            textAlign: { xs: "center", sm: "left" },
            "&:last-child": { pb: { xs: 1, sm: 1.25 } },
          }}
        >
          <Box
            sx={{
              maxHeight: BIRTHDAY_BANNER_MAX_HEIGHT,
              ...dashboardScrollSx(isDarkMode),
            }}
          >
            <Grid container spacing={{ xs: 0.75, sm: 1 }}>
              {todayList.length > 0 && (
                <Grid item xs={12} md={tomorrowList.length > 0 ? 6 : 12}>
                  <BirthdayDayPanel
                    title={t("staff_dashboard_birthdays_today")}
                    dateLabel={todayLabel}
                    people={todayList}
                    isDarkMode={isDarkMode}
                    t={t}
                    emptyLabel={t("staff_dashboard_birthdays_none_today")}
                  />
                </Grid>
              )}
              {tomorrowList.length > 0 && (
                <Grid item xs={12} md={todayList.length > 0 ? 6 : 12}>
                  <BirthdayDayPanel
                    title={t("staff_dashboard_birthdays_tomorrow")}
                    dateLabel={tomorrowLabel}
                    people={tomorrowList}
                    isDarkMode={isDarkMode}
                    t={t}
                    emptyLabel={t("staff_dashboard_birthdays_none_tomorrow")}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </CardContent>
      </Collapse>
    </Card>
  );
}

export default function StaffDailyDashboard({
  isDarkMode,
  userName,
  setSelectedPage,
  isAdmin = false,
  clubID,
}) {
  const { t, i18n } = useTranslation();
  const { toolbarButtonSx } = useSystemButtonStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsedCards, setCollapsedCards] = useState(() => readCollapsedCards(clubID));

  useEffect(() => {
    setCollapsedCards(readCollapsedCards(clubID));
  }, [clubID]);

  const collapseLabel = t("staff_dashboard_collapse_card");
  const expandLabel = t("staff_dashboard_expand_card");
  const isCollapsible = isAdmin === true;

  const toggleCardCollapse = useCallback(
    (cardId) => {
      setCollapsedCards((prev) => {
        const next = { ...prev, [cardId]: !prev[cardId] };
        writeCollapsedCards(clubID, next);
        return next;
      });
    },
    [clubID]
  );

  const isCardCollapsed = useCallback(
    (cardId) => isCollapsible && Boolean(collapsedCards[cardId]),
    [collapsedCards, isCollapsible]
  );

  const todayLabel = useMemo(() => {
    const dateStr = data?.date || new Date().toISOString().slice(0, 10);
    const dt = new Date(`${dateStr}T12:00:00`);
    return dt.toLocaleDateString(i18n.language || undefined, {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  }, [data?.date, i18n.language]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchDailyDashboard();
      setData(result);
    } catch (err) {
      setError(err.message || t("staff_dashboard_load_error"));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const sections = data?.sections || {};
  const viewMoreLabel = t("staff_dashboard_view_more");
  const navigateToPage = useCallback(
    (page) => {
      if (setSelectedPage) setSelectedPage(page);
    },
    [setSelectedPage]
  );
  const navigateToFinancePayCash = useCallback(() => {
    try {
      sessionStorage.setItem("financeInitialBillingFilter", "PAY_CASH");
    } catch {
      /* ignore */
    }
    navigateToPage("FinancePage");
  }, [navigateToPage]);
  const tomorrowLabel = useMemo(() => {
    const dateStr =
      sections.birthdays?.tomorrowDate ||
      (() => {
        const base = data?.date || new Date().toISOString().slice(0, 10);
        const [y, m, d] = base.split("-").map(Number);
        const dt = new Date(y, m - 1, d + 1);
        return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
      })();
    const dt = new Date(`${dateStr}T12:00:00`);
    return dt.toLocaleDateString(i18n.language || undefined, {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  }, [sections.birthdays?.tomorrowDate, data?.date, i18n.language]);

  const paymentsViewPage =
    sections.paymentsToday?.membership != null
      ? "FinancePage"
      : sections.paymentsToday?.pos != null
        ? "PosAdminPage"
        : "FinancePage";

  const sharedCardProps = {
    isCollapsible,
    onToggleCollapse: toggleCardCollapse,
    collapseLabel,
    expandLabel,
  };

  const attendanceKpi = useMemo(() => {
    const a = sections.attendanceSummary;
    if (!a) return null;
    return {
      value: a.uniqueUsers ?? 0,
      subtitle: `${t("staff_dashboard_total_visits")}: ${a.totalVisits ?? 0} · ${t("staff_dashboard_regular_visits")}: ${a.regularVisits ?? 0} · ${t("staff_dashboard_instructor_visits")}: ${a.instructorVisits ?? 0}`,
    };
  }, [sections.attendanceSummary, t]);

  const paymentsKpi = useMemo(() => {
    const p = sections.paymentsToday;
    if (!p) return null;
    const parts = [];
    if (p.membership != null) {
      parts.push(
        `${t("staff_dashboard_membership_payments")}: ${formatMoney(p.membership.totalAmount, p.currency, i18n.language)}`
      );
    }
    if (p.pos != null) {
      parts.push(
        `${t("staff_dashboard_pos_revenue")}: ${formatMoney(p.pos.totalAmount, p.currency, i18n.language)}`
      );
    }
    return {
      value: formatMoney(p.combinedTotal, p.currency, i18n.language),
      subtitle: parts.join(" · ") || t("staff_dashboard_combined_total"),
    };
  }, [sections.paymentsToday, i18n.language, t]);

  const payCashCount = sections.payCashPending?.count ?? sections.payCashPending?.members?.length ?? 0;
  const promotionsPendingCount =
    typeof sections.promotionsPending === "number" ? sections.promotionsPending : 0;

  const showBirthdaysCard =
    (sections.birthdays?.today?.length ?? 0) > 0 ||
    (sections.birthdays?.tomorrow?.length ?? 0) > 0;

  if (loading && !data) {
    return <PageLoader message={t("loading_text")} />;
  }

  const classesSectionTitle = isMobile
    ? t("staff_dashboard_classes_today")
    : t("staff_dashboard_classes_on_date", { date: todayLabel });

  return (
    <Box sx={{ mb: { xs: 2, sm: 2.5 }, fontFamily: getMm(isDarkMode).fontBody, minWidth: 0, width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 1.25,
          mb: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              ...getPageTitleSx(isDarkMode),
              fontSize: { xs: "1.375rem", sm: "28px" },
              lineHeight: 1.25,
            }}
          >
            {t("staff_dashboard_title")}
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              color: getMm(isDarkMode).onSurfaceVariant,
              fontSize: { xs: "0.875rem", sm: "1rem" },
              fontFamily: getMm(isDarkMode).fontBody,
              lineHeight: 1.45,
              wordBreak: "break-word",
            }}
          >
            {userName
              ? t("staff_dashboard_subtitle_named", { name: userName, date: todayLabel })
              : t("staff_dashboard_subtitle", { date: todayLabel })}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadDashboard}
          disabled={loading}
          sx={{
            ...toolbarButtonSx,
            ...getFocusVisibleSx(isDarkMode),
            cursor: "pointer",
            alignSelf: { xs: "stretch", sm: "center" },
            minHeight: 44,
            flexShrink: 0,
          }}
        >
          {t("staff_dashboard_refresh")}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={{ xs: 1.25, sm: 1.5 }} alignItems="stretch" sx={{ width: "100%", minWidth: 0 }}>
        {showBirthdaysCard && (
          <Grid item xs={12} sx={{ display: "flex" }}>
            <BirthdaysBannerCard
              birthdays={sections.birthdays}
              isDarkMode={isDarkMode}
              t={t}
              todayLabel={todayLabel}
              tomorrowLabel={tomorrowLabel}
              collapsed={isCardCollapsed("birthdays")}
              {...sharedCardProps}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Box
            sx={{
              display: "grid",
              width: "100%",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
              },
              gap: { xs: 1.25, sm: 1.5 },
            }}
          >
            <Box sx={{ display: "flex", minWidth: 0 }}>
              <DashboardKpiCard
                title={t("staff_dashboard_attendance_summary")}
                value={attendanceKpi?.value ?? 0}
                subtitle={attendanceKpi?.subtitle ?? t("staff_dashboard_attendance_empty")}
                icon={<HowToRegIcon />}
                iconVariant="primary"
                isDarkMode={isDarkMode}
                onClick={() => navigateToPage("ClubAttendanceStats")}
                actionLabel={viewMoreLabel}
              />
            </Box>
            <Box sx={{ display: "flex", minWidth: 0 }}>
              <DashboardKpiCard
                title={t("staff_dashboard_payments_today")}
                value={
                  paymentsKpi?.value ??
                  formatMoney(0, sections.paymentsToday?.currency, i18n.language)
                }
                subtitle={paymentsKpi?.subtitle ?? t("staff_dashboard_payments_empty")}
                icon={<PaymentsIcon />}
                iconVariant="success"
                isDarkMode={isDarkMode}
                onClick={() => navigateToPage(paymentsViewPage)}
                actionLabel={viewMoreLabel}
              />
            </Box>
            <Box sx={{ display: "flex", minWidth: 0 }}>
              <DashboardKpiCard
                title={t("staff_dashboard_pay_cash_short")}
                value={payCashCount}
                subtitle={payCashCount > 0 ? undefined : t("staff_dashboard_pay_cash_none")}
                icon={<LocalAtmIcon />}
                iconVariant="warning"
                isDarkMode={isDarkMode}
                onClick={payCashCount > 0 ? navigateToFinancePayCash : undefined}
                actionLabel={payCashCount > 0 ? viewMoreLabel : undefined}
              />
            </Box>
            <Box sx={{ display: "flex", minWidth: 0 }}>
              <DashboardKpiCard
                title={t("promotions")}
                value={promotionsPendingCount}
                subtitle={t("staff_dashboard_promotions_pending")}
                icon={<EmojiEventsIcon />}
                iconVariant="violet"
                isDarkMode={isDarkMode}
                onClick={() => navigateToPage("BeltPromotionPage")}
                actionLabel={viewMoreLabel}
              />
            </Box>
          </Box>
        </Grid>

        {sections.classesToday && (
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <DashboardSectionCard
              cardId="classesToday"
              title={classesSectionTitle}
              icon={<EventAvailableIcon />}
              iconVariant="primary"
              isDarkMode={isDarkMode}
              viewPage="SchedulePage"
              onNavigate={navigateToPage}
              viewMoreLabel={viewMoreLabel}
              collapsed={isCardCollapsed("classesToday")}
              {...sharedCardProps}
            >
              {sections.classesToday.length === 0 ? (
                <Box sx={{ py: 1 }}>
                  <Typography sx={{ color: getMm(isDarkMode).onSurfaceVariant, fontSize: "0.875rem", mb: 1.25 }}>
                    {t("staff_dashboard_no_classes_on_date")}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigateToPage("SchedulePage")}
                    sx={{ ...toolbarButtonSx, ...getFocusVisibleSx(isDarkMode), cursor: "pointer" }}
                  >
                    {viewMoreLabel}
                  </Button>
                </Box>
              ) : (
                sections.classesToday.map((cls, index) => (
                  <Box key={cls.id ?? `${cls.title}-${index}`} sx={{ mb: index < sections.classesToday.length - 1 ? 1 : 0 }}>
                    {index > 0 && (
                      <Divider sx={{ mb: 1, borderColor: getMm(isDarkMode).outlineVariant }} />
                    )}
                    <ScheduleClassRow cls={cls} isDarkMode={isDarkMode} t={t} />
                  </Box>
                ))
              )}
            </DashboardSectionCard>
          </Grid>
        )}

        {sections.instructorToday && (
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <DashboardSectionCard
              cardId="instructorToday"
              title={t("staff_dashboard_instructor_classes")}
              icon={<SchoolIcon />}
              iconVariant="violet"
              isDarkMode={isDarkMode}
              viewPage="InstructorAvailabilityPage"
              onNavigate={navigateToPage}
              viewMoreLabel={viewMoreLabel}
              collapsed={isCardCollapsed("instructorToday")}
              {...sharedCardProps}
            >
              {sections.instructorToday.length === 0 ? (
                <Box sx={{ py: 1 }}>
                  <Typography sx={{ color: getMm(isDarkMode).onSurfaceVariant, fontSize: "0.875rem", mb: 1.25 }}>
                    {t("staff_dashboard_no_classes_today")}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigateToPage("InstructorAvailabilityPage")}
                    sx={{ ...toolbarButtonSx, ...getFocusVisibleSx(isDarkMode), cursor: "pointer" }}
                  >
                    {viewMoreLabel}
                  </Button>
                </Box>
              ) : (
                sections.instructorToday.map((cls, index) => (
                  <Box key={cls.id ?? `${cls.title}-${index}`} sx={{ mb: index < sections.instructorToday.length - 1 ? 1 : 0 }}>
                    {index > 0 && (
                      <Divider sx={{ mb: 1, borderColor: getMm(isDarkMode).outlineVariant }} />
                    )}
                    <InstructorClassRow cls={cls} isDarkMode={isDarkMode} t={t} />
                  </Box>
                ))
              )}
            </DashboardSectionCard>
          </Grid>
        )}

      </Grid>
    </Box>
  );
}
